if (!customElements.get('cart-recommendations-popup')) {
  customElements.define(
    'cart-recommendations-popup',
    class CartRecommendationsPopup extends HTMLElement {
      constructor() {
        super();
        this.panel = this.querySelector('.cart-recommendations-popup__panel');
        this.dynamic = this.querySelector('[data-popup-dynamic]');
        this.status = this.querySelector('[data-popup-status]');
        this.abortController = null;
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      getSectionsToRender() {
        return [{ id: 'cart-icon-bubble' }];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      async renderContents(parsedState) {
        this.updateCartBubble(parsedState);

        const handle = parsedState.handle || parsedState.product_handle;
        const variantId = parsedState.variant_id || parsedState.id;
        if (!handle) return;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        this.classList.add('is-loading');
        this.open();

        try {
          const productPath = `${this.dataset.rootUrl}products/${encodeURIComponent(handle)}`;
          const requestUrl = new URL(productPath, window.location.origin);
          requestUrl.searchParams.set('section_id', this.dataset.sectionId);
          if (variantId) requestUrl.searchParams.set('variant', variantId);

          const response = await fetch(requestUrl.toString(), {
            signal: this.abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Unable to load cart recommendations (${response.status})`);

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextDynamic = documentFragment.querySelector('[data-popup-dynamic]');
          if (!nextDynamic) throw new Error('Cart recommendations markup is missing');

          this.dynamic.innerHTML = nextDynamic.innerHTML;
          this.updateFreeShippingProgress();
          this.classList.remove('is-loading');
          this.loadFallbackProducts(handle);
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.classList.remove('is-loading');
          this.close();
          console.error(error);
        }
      }

      updateCartBubble(parsedState) {
        const sectionHtml = parsedState.sections && parsedState.sections['cart-icon-bubble'];
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (!sectionHtml || !currentBubble) return;

        const nextBubble = new DOMParser()
          .parseFromString(sectionHtml, 'text/html')
          .querySelector('#cart-icon-bubble');
        if (nextBubble) currentBubble.innerHTML = nextBubble.innerHTML;
      }

      async loadFallbackProducts(currentProductHandle) {
        const fallback = this.querySelector('[data-popup-fallback]');
        if (!fallback) return;

        const list = fallback.querySelector('[data-popup-fallback-list]');
        if (!list) return;

        try {
          const requestUrl = new URL(this.dataset.bestSellersUrl, window.location.origin);
          requestUrl.searchParams.set('sort_by', 'best-selling');
          requestUrl.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(requestUrl.toString(), {
            signal: this.abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Unable to load best-selling products (${response.status})`);

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const source = documentFragment.querySelector('[data-popup-bestsellers-source]');
          if (!source) throw new Error('Best-selling products markup is missing');

          const limit = Number.parseInt(this.dataset.fallbackLimit, 10) || 3;
          const cards = [...source.querySelectorAll('[data-popup-product-handle]')]
            .filter((card) => card.dataset.popupProductHandle !== currentProductHandle)
            .slice(0, limit)
            .map((card) => document.importNode(card, true));

          if (!cards.length) throw new Error('No best-selling products are available');

          list.replaceChildren(...cards);
          list.setAttribute('aria-busy', 'false');
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.renderFallbackEmpty(list, fallback.dataset.emptyMessage);
          console.warn(error);
        }
      }

      renderFallbackEmpty(list, message) {
        const empty = document.createElement('p');
        empty.className = 'cart-recommendations-popup__empty';
        empty.textContent = message;
        list.replaceChildren(empty);
        list.setAttribute('aria-busy', 'false');
      }

      updateFreeShippingProgress(totalCents = null) {
        const progress = this.querySelector('[data-free-shipping-progress]');
        if (!progress) return;

        const threshold = Number(progress.dataset.thresholdCents);
        const total = totalCents === null ? Number(progress.dataset.cartTotalCents) : Number(totalCents);
        if (!Number.isFinite(threshold) || !Number.isFinite(total) || threshold <= 0) return;

        const safeTotal = Math.max(0, total);
        const remaining = Math.max(0, threshold - safeTotal);
        const percentage = Math.min(100, (safeTotal / threshold) * 100);
        const isComplete = remaining === 0;
        const message = progress.querySelector('[data-free-shipping-message]');
        const fill = progress.querySelector('[data-free-shipping-fill]');
        const progressbar = progress.querySelector('[role="progressbar"]');

        progress.dataset.cartTotalCents = String(safeTotal);
        progress.classList.toggle('is-complete', isComplete);
        if (fill) fill.style.width = `${percentage}%`;
        if (progressbar) progressbar.setAttribute('aria-valuenow', String(Math.min(safeTotal, threshold)));
        if (message) {
          message.textContent = isComplete
            ? progress.dataset.reachedTemplate
            : progress.dataset.remainingTemplate.replace('[amount]', this.formatMoney(remaining, progress));
        }
      }

      formatMoney(cents, progress) {
        const currency = progress.dataset.currency || 'PLN';
        const locale = progress.dataset.locale || document.documentElement.lang || 'pl';

        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2)} ${currency}`;
        }
      }

      async refreshFreeShippingProgress() {
        if (!this.querySelector('[data-free-shipping-progress]')) return;

        const response = await fetch(`${this.dataset.rootUrl}cart.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Unable to refresh cart total (${response.status})`);

        const cart = await response.json();
        this.updateFreeShippingProgress(cart.total_price);
      }

      open() {
        this.hidden = false;
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          document.body.classList.add('cart-recommendations-popup-open');
          document.addEventListener('keydown', this.handleKeydown);
          this.panel.focus();
        });
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('cart-recommendations-popup-open');
        document.removeEventListener('keydown', this.handleKeydown);
        window.setTimeout(() => {
          this.hidden = true;
          if (this.activeElement && document.contains(this.activeElement)) this.activeElement.focus();
        }, 260);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleClick(event) {
        if (event.target.closest('[data-popup-close]')) {
          this.close();
          return;
        }

        const addButton = event.target.closest('[data-popup-add]');
        if (addButton) this.addRecommendedProduct(addButton);
      }

      async addRecommendedProduct(button) {
        if (button.disabled || button.classList.contains('is-loading')) return;

        button.classList.add('is-loading');
        button.disabled = true;

        try {
          const formData = new FormData();
          formData.append('id', button.dataset.variantId);
          formData.append('quantity', '1');
          formData.append('sections', 'cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);

          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: formData,
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add product');

          this.updateCartBubble(result);
          try {
            await this.refreshFreeShippingProgress();
          } catch (progressError) {
            console.warn(progressError);
          }
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          this.status.textContent = button.closest('.cart-recommendations-popup__card')
            .querySelector('.cart-recommendations-popup__product-title').textContent;
        } catch (error) {
          console.error(error);
          button.disabled = false;
          button.classList.remove('is-loading');
          button.classList.add('has-error');
        }
      }
    }
  );
}
